import React, { useState } from 'react';
import { AlertTriangle, Check, ChevronRight, Loader2, X } from 'lucide-react';
import {
  useResolverWarningUnidades,
  useWarningsUnidades,
} from '../hooks/useIngredientes';

const tipoWarningLabel: Record<string, string> = {
  unidad_medida_ambigua: 'Unidad no reconocida',
  ingrediente_sin_unidad_base: 'Ingrediente sin unidad base',
  factor_conversion_faltante: 'Falta factor de conversión',
  unidad_detalle_desconocida: 'Unidad de factura desconocida',
  conversion_incompatible: 'Conversión incompatible',
  unidad_receta_desconocida: 'Unidad de receta no reconocida',
  tipo_fisico_incompatible: 'Tipo físico incompatible',
};

export const WarningsUnidadesBanner: React.FC = () => {
  const { data: warnings, isLoading } = useWarningsUnidades();
  const resolver = useResolverWarningUnidades();
  const [abierto, setAbierto] = useState(false);

  if (isLoading) return null;
  if (!warnings || warnings.length === 0) return null;

  return (
    <>
      {/* Banner compacto */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 border-yellow-400 border-t border-r border-b border-gray-200/50 dark:border-gray-700/50 p-4 mb-6 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="bg-yellow-100 dark:bg-yellow-900/30 p-2 rounded-lg shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {warnings.length}{' '}
              {warnings.length === 1
                ? 'advertencia de unidades'
                : 'advertencias de unidades'}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Ingredientes, facturas o recetas con datos de unidad
              inconsistentes tras la migración. Revisa y corrige para que el
              stock base quede cuadrado.
            </p>
          </div>
        </div>
        <button
          onClick={() => setAbierto(true)}
          className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-yellow-100 hover:bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 dark:text-yellow-300 transition-colors shrink-0"
        >
          Ver detalle
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>

      {/* Modal */}
      {abierto && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setAbierto(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Advertencias de unidades sin resolver
                </h3>
              </div>
              <button
                onClick={() => setAbierto(false)}
                className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-2">
              {warnings.map((w) => (
                <div
                  key={w.id}
                  className="bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200/60 dark:border-gray-700/60 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                          {tipoWarningLabel[w.tipo_warning] ?? w.tipo_warning}
                        </span>
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          {w.entidad}
                          {w.entidad_id ? ` #${w.entidad_id}` : ''}
                        </span>
                        {w.referencia && (
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {w.referencia}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 break-words">
                        {w.detalle}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {new Date(w.created_at).toLocaleString('es-CL')}
                      </p>
                    </div>
                    <button
                      onClick={() => resolver.mutate(w.id)}
                      disabled={resolver.isPending}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-300 transition-colors disabled:opacity-50 shrink-0"
                    >
                      {resolver.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Resolver
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end p-5 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setAbierto(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WarningsUnidadesBanner;
